import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { WithData, WithMeta } from './api.types';

/** Espeja `enum CardArtType` del API (y `CardArtType` de helpers/card-versions). */
export type CardArtType = 'OVERFRAME' | 'ALTERNATE_ART';

// Types for marketplace
export interface PublicSeller {
  id: number;
  name: string;
  lastName: string;
  picture: string | null;
  city: string | null;
  province: {
    id: number;
    name: string;
    country: {
      id: number;
      name: string;
    };
  } | null;
  salableItemsCount: number;
  // Sólo los trae `publicSellers`. El vendedor anidado en un listing viene sin
  // ellos: son la tarjeta del vendedor, no su perfil.
  salesCount: number | null;
  purchasesCount: number | null;
  createdAt: string;
}

export interface PublicListing {
  id: number;
  quantity: number;
  language: string;
  state: string;
  rarity: string;
  salePrice: number;
  card: {
    id: number;
    konamiId: number;
    name: string;
    image: string;
    type: string;
    attribute: string;
    description: string;
  };
  cardSet: {
    id: number;
    code: string;
    rarity: string;
    overframe?: boolean;
    alternateArt?: boolean;
    set: {
      id: number;
      konamiName: string;
      code: string;
    };
  };
  seller: PublicSeller;
  hasShipping?: boolean;
  hasPickup?: boolean;
}

export type SellerVerificationStatus =
  | 'NOT_VERIFIED'
  | 'COMMUNITY_VERIFIED'
  | 'FULLY_VERIFIED'
  | 'PROFESSIONAL_SELLER';

export interface PublicBundleItem {
  konamiId: number;
  cardSetId: number;
  rarity: string;
  language: string;
  state: string;
  quantity: number;
  referencePrice: number;
  card?: {
    konamiId: number;
    name: string;
    image?: string;
  };
  cardSet?: {
    id: number;
    code: string;
    set?: {
      konamiName: string;
    };
  };
}

export interface PublicBundle {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  salePrice: number;
  priceType: string;
  isNegotiable: boolean;
  referenceTotal: number;
  items: PublicBundleItem[];
  seller: PublicSeller;
  hasShipping?: boolean;
  hasPickup?: boolean;
  createdAt: string;
}

export interface PublicBundlesFilters {
  sellerId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PublicSellerDetail extends PublicSeller {
  collectionItemsCount: number;
  salesCount: number;
  purchasesCount: number;
  verificationStatus: SellerVerificationStatus;
  inventory: PublicListing[];
  collection: PublicListing[];
  bundles: PublicBundle[];
  publicEmail: string | null;
}

export interface PublicCardWithListings {
  card: {
    id: number;
    konamiId: number;
    name: string;
    image: string;
    type: string;
    attribute: string;
    description: string;
  };
  cardSet: {
    id: number;
    code: string;
    rarity: string;
    overframe?: boolean;
    alternateArt?: boolean;
    set: {
      id: number;
      konamiName: string;
      code: string;
    };
  };
  listingsCount: number;
  minPrice: number;
  maxPrice: number;
  sellersCount: number;
}

export interface PublicSellersFilters {
  search?: string;
  provinceId?: number;
  paymentMethods?: string[];
  shippingMethods?: string[];
  hasPickup?: boolean;
  page?: number;
  limit?: number;
}

export interface PublicListingsFilters {
  cardId?: number;
  konamiId?: number;
  cardSetId?: number;
  sellerId?: number;
  provinceId?: number;
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  page?: number;
  limit?: number;
}

export interface PublicCardsWithListingsFilters {
  search?: string;
  setId?: number;
  rarity?: string;
  artTypes?: CardArtType[];
  page?: number;
  limit?: number;
}

export interface PublicSet {
  id: number;
  konamiName: string;
  code: string;
  image: string | null;
  releaseDate: string | null;
  numberOfCards: number | null;
}

export interface PublicRarityVariant {
  rarity: string;
  cardSetId: number;
  code: string;
  overframe: boolean;
  alternateArt: boolean;
  image: string | null;
  listingsCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  sellersCount: number;
  // Provider IDs (for card set images)
  cardTraderId: number | null;
  cardMarketId: number | null;
  // Reference prices from external providers
  refTcgPlayer: number | null;
  refCardTrader: number | null;
  refCardMarket: number | null;
}

export interface PublicSetCard {
  card: {
    id: number;
    konamiId: number;
    name: string;
    image: string;
    type: string;
    frameType?: string | null;
    attribute: string;
    description: string;
  };
  code: string;
  rarities: PublicRarityVariant[];
  totalListings: number;
  minPrice: number | null;
  maxPrice: number | null;
  totalSellers: number;
}

export interface PublicLatestSet {
  set: PublicSet;
  cards: PublicSetCard[];
  totalCards: number;
  missingImagesCount: number;
  availableRarities: string[];
  availableArtTypes: CardArtType[];
}

export interface PublicSetOption {
  id: number;
  konamiName: string;
  code: string;
  releaseDate: string | null;
  cardsCount: number;
}

export interface PublicProviderPrice {
  cardSetId: number;
  code: string;
  rarity: string;
  overframe: boolean;
  alternateArt: boolean;
  setName: string;
  image: string | null;
  cardTraderId: number | null;
  cardMarketId: number | null;
  tcgPlayer: number | null;
  cardTrader: number | null;
  cardTraderMin: number | null;
  cardTraderMax: number | null;
  cardMarket: number | null;
  cardMarketLow: number | null;
  cardMarketTrend: number | null;
  updatedAt: string | null;
  tcgPlayerUrl: string | null;
  cardTraderUrl: string | null;
  cardMarketUrl: string | null;
}

export interface PublicCardDetail {
  card: {
    id: number;
    konamiId: number;
    name: string;
    image: string;
    type: string;
    attribute: string;
    description: string;
  };
  listingsCount: number;
  minPrice: number | null;
  maxPrice: number | null;
  sellersCount: number;
  listings: PublicListing[];
  providerPrices: PublicProviderPrice[] | null;
}

export interface PublicSearchCardsResult {
  cards: PublicSetCard[];
  total: number;
}

// Fragment for seller data
const SELLER_FRAGMENT = gql`
  fragment SellerFields on PublicSeller {
    id
    name
    lastName
    picture
    city
    province {
      id
      name
      country {
        id
        name
      }
    }
    salableItemsCount
    salesCount
    purchasesCount
    createdAt
  }
`;

// Fragment for public listing data
const PUBLIC_LISTING_FRAGMENT = gql`
  fragment PublicListingFields on PublicListing {
    id
    quantity
    language
    state
    rarity
    salePrice
    hasShipping
    hasPickup
    card {
      id
      konamiId
      name
      image
      type
      attribute
      description
    }
    cardSet {
      id
      code
      rarity
      overframe
      alternateArt
      set {
        id
        konamiName
        code
      }
    }
    seller {
      id
      name
      lastName
      picture
      city
      province {
        id
        name
      }
      salableItemsCount
      createdAt
    }
  }
`;

// Get list of sellers (users with salable items)
export const getPublicSellers = async (filters?: PublicSellersFilters) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicSeller>>(
    gql`
      ${SELLER_FRAGMENT}
      query publicSellers($filters: PublicSellersFilters) {
        data: publicSellers(filters: $filters) {
          data {
            ...SellerFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

// Get a specific seller with their inventory
export const getPublicSeller = async (id: number) =>
  getGraphQLQueryClient(null).request<WithData<PublicSellerDetail>>(
    gql`
      ${PUBLIC_LISTING_FRAGMENT}
      query publicSeller($id: Int!) {
        data: publicSeller(id: $id) {
          id
          name
          lastName
          picture
          city
          province {
            id
            name
            country {
              id
              name
            }
          }
          salableItemsCount
          collectionItemsCount
          salesCount
          purchasesCount
          verificationStatus
          createdAt
          inventory {
            ...PublicListingFields
          }
          collection {
            ...PublicListingFields
          }
          bundles {
            id
            name
            description
            quantity
            salePrice
            priceType
            isNegotiable
            referenceTotal
            items {
              konamiId
              cardSetId
              rarity
              language
              state
              quantity
              referencePrice
              card {
                konamiId
                name
                image
              }
              cardSet {
                id
                code
                set {
                  konamiName
                }
              }
            }
            seller {
              id
              name
              lastName
              picture
            }
            hasShipping
            hasPickup
            createdAt
          }
          publicEmail
        }
      }
    `,
    { id }
  );

// Get listings with filters
export const getPublicListings = async (filters?: PublicListingsFilters) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicListing>>(
    gql`
      ${PUBLIC_LISTING_FRAGMENT}
      query publicListings($filters: PublicListingsFilters) {
        data: publicListings(filters: $filters) {
          data {
            ...PublicListingFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

// Search cards that have listings
export const getPublicCardsWithListings = async (
  filters?: PublicCardsWithListingsFilters
) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicCardWithListings>>(
    gql`
      query publicCardsWithListings(
        $filters: PublicCardsWithListingsFilters
      ) {
        data: publicCardsWithListings(filters: $filters) {
          data {
            card {
              id
              konamiId
              name
              image
              type
              attribute
              description
            }
            cardSet {
              id
              code
              rarity
              overframe
              alternateArt
              set {
                id
                konamiName
                code
              }
            }
            listingsCount
            minPrice
            maxPrice
            sellersCount
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

const SET_CARDS_FRAGMENT = gql`
  fragment SetCardsFields on PublicLatestSet {
    set {
      id
      konamiName
      code
      image
      releaseDate
      numberOfCards
    }
    cards {
      card {
        id
        konamiId
        name
        image
        type
        attribute
        description
      }
      code
      rarities {
        rarity
        cardSetId
        code
        overframe
        alternateArt
        image
        listingsCount
        minPrice
        maxPrice
        sellersCount
        cardTraderId
        cardMarketId
        refTcgPlayer
        refCardTrader
        refCardMarket
      }
      totalListings
      minPrice
      maxPrice
      totalSellers
    }
    totalCards
    missingImagesCount
    availableRarities
    availableArtTypes
  }
`;

// Get the latest set with its cards
export const getPublicLatestSet = async (
  limit: number = 20,
  rarities?: string[],
  artTypes?: CardArtType[]
) =>
  getGraphQLQueryClient(null).request<{ data: PublicLatestSet | null }>(
    gql`
      ${SET_CARDS_FRAGMENT}
      query publicLatestSet(
        $limit: Int
        $rarities: [String!]
        $artTypes: [CardArtType!]
      ) {
        data: publicLatestSet(
          limit: $limit
          rarities: $rarities
          artTypes: $artTypes
        ) {
          ...SetCardsFields
        }
      }
    `,
    { limit, rarities, artTypes }
  );

// Get a specific set with its cards
export const getPublicSet = async (
  id: number,
  limit: number = 20,
  rarities?: string[],
  artTypes?: CardArtType[]
) =>
  getGraphQLQueryClient(null).request<{ data: PublicLatestSet | null }>(
    gql`
      ${SET_CARDS_FRAGMENT}
      query publicSet(
        $id: Int!
        $limit: Int
        $rarities: [String!]
        $artTypes: [CardArtType!]
      ) {
        data: publicSet(
          id: $id
          limit: $limit
          rarities: $rarities
          artTypes: $artTypes
        ) {
          ...SetCardsFields
        }
      }
    `,
    { id, limit, rarities, artTypes }
  );

// Get list of available sets for selector
export const getPublicSets = async (limit?: number) =>
  getGraphQLQueryClient(null).request<{ data: { data: PublicSetOption[] } }>(
    gql`
      query publicSets($limit: Int) {
        data: publicSets(limit: $limit) {
          data {
            id
            konamiName
            code
            releaseDate
            cardsCount
          }
        }
      }
    `,
    { limit }
  );

// Get card detail with all listings by konamiId
export const getPublicCardDetail = async (konamiId: number) =>
  getGraphQLQueryClient(null).request<{ data: PublicCardDetail | null }>(
    gql`
      ${PUBLIC_LISTING_FRAGMENT}
      query publicCardDetail($konamiId: Int!) {
        data: publicCardDetail(konamiId: $konamiId) {
          card {
            id
            konamiId
            name
            image
            type
            attribute
            description
          }
          listingsCount
          minPrice
          maxPrice
          sellersCount
          listings {
            ...PublicListingFields
          }
          providerPrices {
            cardSetId
            code
            rarity
            overframe
            alternateArt
            setName
            image
            cardTraderId
            cardMarketId
            tcgPlayer
            cardTrader
            cardTraderMin
            cardTraderMax
            cardMarket
            cardMarketLow
            cardMarketTrend
            updatedAt
            tcgPlayerUrl
            cardTraderUrl
            cardMarketUrl
          }
        }
      }
    `,
    { konamiId }
  );

// Search cards by name across all catalog
export const searchPublicCards = async (search: string, limit: number = 20) =>
  getGraphQLQueryClient(null).request<{ data: PublicSearchCardsResult }>(
    gql`
      query publicSearchCards($search: String!, $limit: Int) {
        data: publicSearchCards(search: $search, limit: $limit) {
          cards {
            card {
              id
              konamiId
              name
              image
              type
              frameType
              attribute
              description
            }
            code
            rarities {
              rarity
              cardSetId
              code
              overframe
              alternateArt
              image
              listingsCount
              minPrice
              maxPrice
              sellersCount
              cardTraderId
              cardMarketId
              refTcgPlayer
              refCardTrader
              refCardMarket
            }
            totalListings
            minPrice
            maxPrice
            totalSellers
          }
          total
        }
      }
    `,
    { search, limit }
  );

// =============================================================================
// PUBLIC BUNDLES
// =============================================================================

const PUBLIC_BUNDLE_FRAGMENT = gql`
  fragment PublicBundleFields on PublicBundle {
    id
    name
    description
    quantity
    salePrice
    priceType
    isNegotiable
    referenceTotal
    items {
      konamiId
      cardSetId
      rarity
      language
      state
      quantity
      referencePrice
      card {
        konamiId
        name
        image
      }
      cardSet {
        id
        code
        set {
          konamiName
        }
      }
    }
    seller {
      id
      name
      lastName
      picture
      city
      province {
        id
        name
      }
      salableItemsCount
      createdAt
    }
    hasShipping
    hasPickup
    createdAt
  }
`;

export const getPublicBundles = async (filters?: PublicBundlesFilters) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicBundle>>(
    gql`
      ${PUBLIC_BUNDLE_FRAGMENT}
      query publicBundles($filters: PublicBundlesFilters) {
        data: publicBundles(filters: $filters) {
          data {
            ...PublicBundleFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getPublicBundle = async (id: number) =>
  getGraphQLQueryClient(null).request<WithData<PublicBundle | null>>(
    gql`
      ${PUBLIC_BUNDLE_FRAGMENT}
      query publicBundle($id: Int!) {
        data: publicBundle(id: $id) {
          ...PublicBundleFields
        }
      }
    `,
    { id }
  );

// =============================================================================
// PUBLIC USER ORDERS (sales & purchases history)
// =============================================================================

export interface PublicOrderReviewSummary {
  overallRating: number;
  communicationRating: number;
  // Sales-specific
  shippingRating?: number;
  productRating?: number;
  // Purchases-specific
  paymentCommitmentRating?: number;
  deliveryCommitmentRating?: number;
  generalComment: string | null;
}

// Una operación liquidada como la ve cualquiera. Sin contraparte y sin monto a
// propósito: el API no los devuelve (ver `PublicUserOrder` en Marketplace.graphql).
export interface PublicUserOrder {
  id: number;
  itemsCount: number;
  completedAt: string | null;
  createdAt: string;
  review: PublicOrderReviewSummary | null;
}

const PUBLIC_USER_ORDER_FRAGMENT = gql`
  fragment PublicUserOrderFields on PublicUserOrder {
    id
    itemsCount
    completedAt
    createdAt
    review {
      overallRating
      communicationRating
      shippingRating
      productRating
      paymentCommitmentRating
      deliveryCommitmentRating
      generalComment
    }
  }
`;

export const getPublicUserSales = async (
  userId: number,
  page?: number,
  limit?: number
) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicUserOrder>>(
    gql`
      ${PUBLIC_USER_ORDER_FRAGMENT}
      query publicUserSales($userId: Int!, $page: Int, $limit: Int) {
        data: publicUserSales(userId: $userId, page: $page, limit: $limit) {
          data {
            ...PublicUserOrderFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { userId, page, limit }
  );

export const getPublicUserPurchases = async (
  userId: number,
  page?: number,
  limit?: number
) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicUserOrder>>(
    gql`
      ${PUBLIC_USER_ORDER_FRAGMENT}
      query publicUserPurchases($userId: Int!, $page: Int, $limit: Int) {
        data: publicUserPurchases(userId: $userId, page: $page, limit: $limit) {
          data {
            ...PublicUserOrderFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { userId, page, limit }
  );

// =============================================================================
// PUBLIC ACCESSORY LISTINGS
// =============================================================================

export interface PublicAccessoryListing {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  quantity: number;
  salePrice: number;
  priceType: string;
  isNegotiable: boolean;
  condition?: string;
  product?: {
    id: number;
    name: string;
    description?: string;
    brand?: string;
    imageUrl?: string;
    productType: string;
  };
  seller: PublicSeller;
  createdAt: string;
}

export interface PublicAccessoryListingsFilters {
  sellerId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

const PUBLIC_ACCESSORY_LISTING_FRAGMENT = gql`
  fragment PublicAccessoryListingFields on PublicAccessoryListing {
    id
    name
    description
    imageUrl
    quantity
    salePrice
    priceType
    isNegotiable
    condition
    product {
      id
      name
      description
      brand
      imageUrl
      productType
    }
    seller {
      id
      name
      lastName
      picture
      city
      province {
        id
        name
        country {
          id
          name
        }
      }
      salableItemsCount
      createdAt
    }
    createdAt
  }
`;

export const getPublicAccessoryListings = async (
  filters?: PublicAccessoryListingsFilters
) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicAccessoryListing>>(
    gql`
      ${PUBLIC_ACCESSORY_LISTING_FRAGMENT}
      query publicAccessoryListings($filters: PublicAccessoryListingsFilters) {
        data: publicAccessoryListings(filters: $filters) {
          data {
            ...PublicAccessoryListingFields
          }
          meta {
            total
            perPage
            page
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const getPublicAccessoryListing = async (id: number) =>
  getGraphQLQueryClient(null).request<WithData<PublicAccessoryListing | null>>(
    gql`
      ${PUBLIC_ACCESSORY_LISTING_FRAGMENT}
      query publicAccessoryListing($id: Int!) {
        data: publicAccessoryListing(id: $id) {
          ...PublicAccessoryListingFields
        }
      }
    `,
    { id }
  );

// Trigger image download for a card missing its image
export const downloadCardImage = async (konamiId: number) =>
  getGraphQLQueryClient(null).request<{
    data: { image: string } | null;
  }>(
    gql`
      query downloadImage($konamiId: Int!) {
        data: downloadImage(konamiId: $konamiId) {
          image
        }
      }
    `,
    { konamiId }
  );
